import { useCallback, useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { AddUserDialog } from "@/components/AddUserDialog";
import { TrendsPanel } from "@/components/TrendsPanel";
import { DealershipsPanel } from "@/components/DealershipsPanel";
import { RepairOrderDialog } from "@/components/RepairOrderDialog";
import { BatchSummary } from "@/components/BatchSummary";
import { roleLabel } from "@/lib/roles";
import { CompareDialog } from "@/components/CompareDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, scoreColor } from "@/lib/roMeta";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Users, ClipboardList, AlertTriangle, Activity, ShieldCheck, History, FileWarning,
  Pencil, UserCog, CheckCircle2, ChevronRight, TrendingUp, Mail, Save, Building2,
  Bot, DollarSign, FileText, Wrench, MapPin, Trash2,
} from "lucide-react";

function EditEmailCell({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(user.email);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/users/${user.id}`, { email: email.trim() });
      toast.success("Login email updated");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update email");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-1.5">
      <span className="font-mono text-xs">{user.email}</span>
      <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setEmail(user.email); }}>
        <AlertDialogTrigger asChild>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-foreground" data-testid={`edit-email-${user.email}`}>
            <Pencil className="h-3 w-3" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent data-testid="edit-email-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Change login email</AlertDialogTitle>
            <AlertDialogDescription>
              Update the sign-in email for {user.name}. Any address is allowed — it does not need to match the dealership domain.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <p className="mb-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">New email</p>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-secondary/40" data-testid="edit-email-input" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="edit-email-cancel">Cancel</AlertDialogCancel>
            <Button onClick={save} disabled={busy || !email.trim()} data-testid="edit-email-save">
              {busy ? "Saving…" : "Save email"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RoleBadge({ role }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "font-mono text-[10px] uppercase tracking-wider",
        role === "admin" ? "border-violet-500/40 bg-violet-500/15 text-violet-300" : "border-blue-500/40 bg-blue-500/15 text-blue-300"
      )}
    >
      {role === "admin" && <UserCog className="mr-1 h-2.5 w-2.5" />} {roleLabel(role)}
    </Badge>
  );
}

export default function AdminConsole() {
  const { user: currentUser } = useAuth();
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [trail, setTrail] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [trends, setTrends] = useState([]);
  const [offenders, setOffenders] = useState({ technicians: [], advisors: [] });
  const [aiUsage, setAiUsage] = useState(null);
  const [techUsage, setTechUsage] = useState([]);
  const [highExp, setHighExp] = useState(null);
  const [minHours, setMinHours] = useState(6);
  const [loginEvents, setLoginEvents] = useState([]);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [domainInput, setDomainInput] = useState("");
  const [savingDomain, setSavingDomain] = useState(false);
  const [resetting, setResetting] = useState("");
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dealerships, setDealerships] = useState([]);
  const [tenantFilter, setTenantFilter] = useState("all");

  useEffect(() => {
    api.get("/dealerships").then((r) => setDealerships(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const t = tenantFilter && tenantFilter !== "all" ? `&tenant_id=${tenantFilter}` : "";
    const tq = tenantFilter && tenantFilter !== "all" ? `?tenant_id=${tenantFilter}` : "";
    const calls = [
      [`/admin/overview${tq}`, setOverview, "overview"],
      ["/users", setUsers, "users"],
      ["/admin/activity", setActivity, "activity"],
      ["/admin/audit-trail", setTrail, "audit trail"],
      [`/admin/flagged${tq}`, setFlagged, "flagged"],
      [`/repair-orders${tq}`, setAllRecords, "records"],
      [`/admin/trends?weeks=12${t}`, setTrends, "trends"],
      [`/admin/offenders${tq}`, setOffenders, "offenders"],
      ["/admin/settings", (d) => setDomainInput(d.allowed_domain || ""), "settings"],
      [`/admin/ai-usage${tq}`, setAiUsage, "AI usage"],
      [`/admin/technician-usage${tq}`, setTechUsage, "technician usage"],
    ];
    const results = await Promise.allSettled(calls.map(([url]) => api.get(url)));
    const failed = [];
    results.forEach((r, i) => {
      const [, setter, name] = calls[i];
      if (r.status === "fulfilled") {
        setter(r.value.data);
      } else {
        failed.push(name);
        console.error(`admin load failed: ${name}`, r.reason?.response?.status, r.reason?.response?.data);
      }
    });
    if (failed.length) toast.error(`Couldn't load: ${failed.join(", ")}`);
  }, [tenantFilter]);

  useEffect(() => { load(); }, [load]);

  const loadHighExp = useCallback(async () => {
    const t = tenantFilter && tenantFilter !== "all" ? `&tenant_id=${tenantFilter}` : "";
    try {
      const { data } = await api.get(`/admin/high-exposure?min_hours=${minHours || 6}${t}`);
      setHighExp(data);
    } catch { /* non-blocking */ }
  }, [tenantFilter, minHours]);
  useEffect(() => { loadHighExp(); }, [loadHighExp]);

  const loadLoginEvents = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/login-events?flagged_only=${flaggedOnly}`);
      setLoginEvents(data);
    } catch { /* non-blocking */ }
  }, [flaggedOnly]);
  useEffect(() => { loadLoginEvents(); }, [loadLoginEvents]);

  const exportExposureCsv = () => {
    const rows = highExp?.rows || [];
    const safe = (v) => {
      let s = String(v ?? "");
      if (/^[=+\-@]/.test(s)) s = `'${s}`;
      return `"${s.replace(/"/g, '""')}"`;
    };
    const head = ["RO #", "Technician #", "Labor Op", "Hours", "Parts", "Vehicle", "Status", "Score", "Reason", "Dealership"];
    const lines = [head.join(",")].concat(rows.map((r) => [
      r.ro_number, r.technicians.join(" | "), r.labor_ops.join(" | "),
      r.labor_hours_total, r.parts_count, r.vehicle, r.compliance_status, r.score, r.reason, r.dealership || "",
    ].map(safe).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "high-exposure-claims.csv";
    a.click();
  };

  const saveDomain = async () => {
    setSavingDomain(true);
    try {
      const { data } = await api.put("/admin/settings", { allowed_domain: domainInput });
      setDomainInput(data.allowed_domain || "");
      toast.success(data.allowed_domain ? `Restricted to @${data.allowed_domain}` : "Restriction removed — any email allowed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSavingDomain(false);
    }
  };

  const sendReset = async (u) => {
    setResetting(u.id);
    try {
      await api.post(`/users/${u.id}/send-reset`);
      toast.success(`Password reset email sent to ${u.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send reset email");
    } finally {
      setResetting("");
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(`${u.name} ${u.active ? "deactivated" : "activated"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update user");
    }
  };

  const reassignDealership = async (u, dealership_id) => {
    try {
      await api.patch(`/users/${u.id}`, { dealership_id });
      toast.success(`${u.name} moved to ${dealerships.find((d) => d.id === dealership_id)?.name || "store"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reassign store");
    }
  };

  const clearActivity = async () => {
    try {
      const { data } = await api.delete("/admin/activity");
      toast.success(`Cleared ${data.deleted} activity event(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clear activity");
    }
  };

  const deleteUser = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Deleted ${u.name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete user");
    }
  };

  const resetMfa = async (u) => {
    try {
      await api.post(`/users/${u.id}/reset-mfa`);
      toast.success(`2FA reset for ${u.name} — they'll set up their authenticator again at next sign-in`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reset 2FA");
    }
  };

  return (
    <AppLayout>
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-6 w-6 text-violet-400" />
        <h1 className="font-heading text-3xl font-extrabold uppercase tracking-tight sm:text-4xl">Admin Console</h1>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">Monitor users, review every audit, flag mistakes, and trace who changed what.</p>

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testid="admin-stat-users" label="Users" value={overview?.user_count ?? "—"} icon={Users} tone="purple" sub={overview ? `${overview.active_users} active` : ""} />
        <StatCard testid="admin-stat-ros" label="Total Audits" value={overview?.total_ros ?? "—"} icon={ClipboardList} tone="primary" />
        <StatCard testid="admin-stat-flagged" label="Flagged Audits" value={overview?.flagged_ros ?? "—"} icon={FileWarning} tone="amber" />
        <StatCard testid="admin-stat-events" label="Activity Events" value={activity.length} icon={Activity} tone="emerald" />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card/70 px-4 py-3" data-testid="dealership-filter-bar">
        <Building2 className="h-4 w-4 text-violet-400" />
        <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Dealership filter</span>
        <Select value={tenantFilter} onValueChange={setTenantFilter}>
          <SelectTrigger className="ml-1 w-64 bg-secondary/40" data-testid="dealership-filter-select">
            <SelectValue placeholder="All dealerships" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="dealership-filter-all">All dealerships</SelectItem>
            {dealerships.map((d) => (
              <SelectItem key={d.id} value={d.id} data-testid={`dealership-filter-${d.id}`}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="ml-1 text-xs text-muted-foreground">Applies to Monitoring, Trends &amp; Audit Records.</span>
      </div>

      <Tabs defaultValue="monitoring" className="mt-4">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-card/80 p-1">
          <TabsTrigger value="monitoring" data-testid="tab-monitoring" className="gap-1.5"><Activity className="h-4 w-4" /> Monitoring</TabsTrigger>
          <TabsTrigger value="trends" data-testid="tab-trends" className="gap-1.5"><TrendingUp className="h-4 w-4" /> Trends</TabsTrigger>
          <TabsTrigger value="dealerships" data-testid="tab-dealerships" className="gap-1.5"><Building2 className="h-4 w-4" /> Dealerships</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users" className="gap-1.5"><Users className="h-4 w-4" /> Users</TabsTrigger>
          <TabsTrigger value="records" data-testid="tab-records" className="gap-1.5"><ClipboardList className="h-4 w-4" /> Audit Records</TabsTrigger>
          <TabsTrigger value="flagged" data-testid="tab-flagged" className="gap-1.5"><AlertTriangle className="h-4 w-4" /> Potential Mistakes</TabsTrigger>
          <TabsTrigger value="trail" data-testid="tab-trail" className="gap-1.5"><History className="h-4 w-4" /> Audit Trail</TabsTrigger>
          <TabsTrigger value="technician" data-testid="tab-technician" className="gap-1.5"><Wrench className="h-4 w-4" /> Technician Usage</TabsTrigger>
          <TabsTrigger value="exposure" data-testid="tab-exposure" className="gap-1.5"><DollarSign className="h-4 w-4" /> High-Exposure</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security" className="gap-1.5"><MapPin className="h-4 w-4" /> Login Security</TabsTrigger>
        </TabsList>

        {/* MONITORING */}
        <TabsContent value="monitoring" className="mt-5 space-y-5">
          <div className="rounded-xl border border-border bg-card/90 shadow-lg">
            <div className="border-b border-border px-5 py-4"><h2 className="font-heading text-xl font-bold uppercase tracking-tight">User Activity Monitor</h2></div>
            <div className="overflow-x-auto">
              <Table data-testid="monitoring-table">
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">User</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Role</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Last Login</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Audits</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Compliant</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Flagged</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overview?.per_user?.map((u) => (
                    <TableRow key={u.id} className="border-border">
                      <TableCell>
                        <span className="font-semibold">{u.name}</span>
                        <span className="block font-mono text-[10px] text-muted-foreground">{u.email}</span>
                      </TableCell>
                      <TableCell><RoleBadge role={u.role} /></TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(u.last_login)}</TableCell>
                      <TableCell className="font-mono">{u.total}</TableCell>
                      <TableCell className="font-mono text-emerald-400">{u.compliant}</TableCell>
                      <TableCell className="font-mono text-amber-400">{u.flagged}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card/90 shadow-lg">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Recent Activity Feed</h2>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="clear-activity-btn" disabled={activity.length === 0}>Clear</Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="clear-activity-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Clear the activity feed?</AlertDialogTitle>
                    <AlertDialogDescription>This permanently removes all {activity.length} activity events. This cannot be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="clear-activity-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={clearActivity} className="bg-red-600 text-white hover:bg-red-700" data-testid="clear-activity-confirm">Clear feed</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            <div className="max-h-[420px] divide-y divide-border overflow-y-auto" data-testid="activity-feed">
              {activity.length === 0 && <p className="p-6 text-sm text-muted-foreground">No activity yet.</p>}
              {activity.map((a) => (
                <div key={a.id} className="flex items-center gap-3 px-5 py-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent">
                    <Activity className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm"><span className="font-semibold">{a.user_name}</span> <span className="text-muted-foreground">{a.detail}</span></p>
                    <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{a.action} · {fmtDate(a.timestamp)}</p>
                  </div>
                  <RoleBadge role={a.role} />
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* TRENDS */}
        <TabsContent value="trends" className="mt-5 space-y-5">
          {aiUsage && (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <StatCard testid="ai-usage-month" label="AI Scans (This Month)" value={aiUsage.month_pdf_count} icon={Bot} tone="purple" />
              <StatCard testid="ai-usage-cost" label="Est. AI Cost (Month)" value={`$${aiUsage.est_cost_usd}`} icon={DollarSign} tone="emerald" sub={`≈ ${aiUsage.est_credits} credits`} />
              <StatCard testid="ai-usage-total" label="Total AI Scans" value={aiUsage.total_pdf_count} icon={FileText} tone="primary" />
              <StatCard testid="ai-usage-per" label="Est. Cost / PDF" value={`$${aiUsage.per_pdf_usd}`} icon={TrendingUp} tone="slate" sub="Gemini Flash estimate" />
            </div>
          )}
          <TrendsPanel trends={trends} offenders={offenders} />
        </TabsContent>

        {/* DEALERSHIPS */}
        <TabsContent value="dealerships" className="mt-5">
          <DealershipsPanel />
        </TabsContent>

        {/* USERS */}
        <TabsContent value="users" className="mt-5 space-y-5">
          <div className="rounded-xl border border-border bg-card/90 p-5 shadow-lg" data-testid="domain-settings-card">
            <div className="mb-3 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-violet-400" />
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight">Dealership Email Restriction</h2>
            </div>
            <p className="mb-3 text-sm text-muted-foreground">
              Restrict new accounts and password resets to your dealership's email domain. Leave blank to allow any email.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center rounded-md border border-border bg-secondary/40 pl-3">
                <span className="font-mono text-sm text-muted-foreground">@</span>
                <Input
                  data-testid="domain-input"
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  placeholder="yourdealership.com"
                  className="w-56 border-0 bg-transparent focus-visible:ring-0"
                />
              </div>
              <Button onClick={saveDomain} disabled={savingDomain} data-testid="save-domain-button">
                <Save className="h-4 w-4" /> {savingDomain ? "Saving…" : "Save"}
              </Button>
              {domainInput ? (
                <Badge variant="outline" className="border-violet-500/40 bg-violet-500/15 font-mono text-[10px] uppercase text-violet-300">
                  restricted to @{domainInput}
                </Badge>
              ) : (
                <Badge variant="outline" className="font-mono text-[10px] uppercase text-muted-foreground">no restriction</Badge>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card/90 shadow-lg">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Manage Users</h2>
              <AddUserDialog onCreated={load} />
            </div>
            <div className="overflow-x-auto">
              <Table data-testid="users-table">
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Name</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Email</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Role</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Created</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Last Login</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Store</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Active</TableHead>
                    <TableHead className="text-right font-mono text-[11px] uppercase tracking-wider">Reset</TableHead>
                    <TableHead className="text-right font-mono text-[11px] uppercase tracking-wider">Delete</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} className="border-border" data-testid={`user-row-${u.email}`}>
                      <TableCell className="font-semibold">{u.name}</TableCell>
                      <TableCell><EditEmailCell user={u} onSaved={load} /></TableCell>
                      <TableCell><RoleBadge role={u.role} /></TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(u.created_at)}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(u.last_login)}</TableCell>
                      <TableCell>
                        <select
                          className="rounded-md border border-border bg-secondary/40 px-2 py-1 text-xs"
                          value={u.tenant_id || ""}
                          onChange={(e) => reassignDealership(u, e.target.value)}
                          data-testid={`user-store-${u.email}`}
                        >
                          <option value="" disabled>Not assigned — pick a dealership</option>
                          {dealerships.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
                        </select>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-active-${u.email}`} />
                          <span className={cn("font-mono text-[11px]", u.active ? "text-emerald-400" : "text-red-400")}>{u.active ? "active" : "off"}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => sendReset(u)}
                            disabled={resetting === u.id}
                            data-testid={`send-reset-${u.email}`}
                          >
                            <Mail className="h-3.5 w-3.5" /> {resetting === u.id ? "Sending…" : "Send reset"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => resetMfa(u)}
                            data-testid={`reset-mfa-${u.email}`}
                          >
                            <ShieldCheck className="h-3.5 w-3.5" /> Reset 2FA
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {u.id === currentUser?.id ? (
                          <span className="font-mono text-[10px] uppercase text-muted-foreground">you</span>
                        ) : (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-red-400" data-testid={`delete-user-${u.email}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent data-testid="delete-user-dialog">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete {u.name}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This permanently removes <span className="font-mono">{u.email}</span> ({roleLabel(u.role)}) and revokes their access. This cannot be undone. Their existing audit records are kept.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel data-testid="delete-user-cancel">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteUser(u)}
                                  className="bg-red-600 text-white hover:bg-red-700"
                                  data-testid="delete-user-confirm"
                                >
                                  Delete user
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* AUDIT RECORDS */}
        <TabsContent value="records" className="mt-5">
          <div className="rounded-xl border border-border bg-card/90 shadow-lg">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight">All Repair Order Audits</h2>
              <div className="flex flex-wrap items-center gap-2">
                <BatchSummary
                  records={allRecords}
                  dealership={tenantFilter !== "all" ? dealerships.find((d) => d.id === tenantFilter)?.name : "All Dealerships"}
                  adminName="Warranty Administrator"
                  logoUrl={tenantFilter !== "all" && dealerships.find((d) => d.id === tenantFilter)?.has_logo ? `${process.env.REACT_APP_BACKEND_URL}/api/dealerships/${tenantFilter}/logo` : null}
                />
                <CompareDialog records={allRecords} />
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table data-testid="admin-records-table">
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">RO #</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Owner</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Vehicle</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Status</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Score</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Claim</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Flags</TableHead>
                    <TableHead className="text-right font-mono text-[11px] uppercase tracking-wider">Review</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allRecords.map((o) => (
                    <TableRow key={o.id} className="border-border">
                      <TableCell className="font-mono font-semibold text-primary">{o.ro_number}</TableCell>
                      <TableCell className="text-sm">{o.owner_name}</TableCell>
                      <TableCell className="text-sm">{[o.vehicle_year, o.vehicle_make, o.vehicle_model].filter(Boolean).join(" ") || "—"}</TableCell>
                      <TableCell><StatusBadge status={o.compliance_status} /></TableCell>
                      <TableCell className={`font-mono text-sm font-bold ${scoreColor(o.score)}`}>{o.score == null ? "—" : o.score}</TableCell>
                      <TableCell className="font-mono text-sm">{o.claim_amount != null ? `$${o.claim_amount}` : "—"}</TableCell>
                      <TableCell className="font-mono text-sm text-amber-400">{o.flags?.length || 0}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => { setEditing(o); setDialogOpen(true); }} data-testid={`admin-review-${o.ro_number}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* FLAGGED / POTENTIAL MISTAKES */}
        <TabsContent value="flagged" className="mt-5 space-y-4">
          {flagged.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card/90 p-14 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-400" />
              <p className="text-sm text-muted-foreground">No potential mistakes detected across any audit. Great work.</p>
            </div>
          ) : (
            flagged.map((o) => (
              <div key={o.id} className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-5 shadow-lg" data-testid={`flagged-card-${o.ro_number}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-400" />
                    <div>
                      <p className="font-heading text-lg font-bold">RO #{o.ro_number} <span className="text-sm font-normal text-muted-foreground">· {o.owner_name}</span></p>
                      <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{[o.vehicle_year, o.vehicle_make, o.vehicle_model].filter(Boolean).join(" ")}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={o.compliance_status} />
                    <Button variant="outline" size="sm" onClick={() => { setEditing(o); setDialogOpen(true); }} data-testid={`flagged-review-${o.ro_number}`}>
                      Review <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                  {o.flags.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 rounded-md border border-amber-500/20 bg-background/40 px-3 py-1.5 text-sm text-amber-200">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </TabsContent>

        {/* AUDIT TRAIL */}
        <TabsContent value="trail" className="mt-5">
          <div className="rounded-xl border border-border bg-card/90 p-5 shadow-lg">
            <h2 className="mb-4 font-heading text-xl font-bold uppercase tracking-tight">Audit Trail — Who Changed What</h2>
            <div className="relative max-h-[560px] space-y-0 overflow-y-auto pl-4" data-testid="audit-trail">
              <div className="absolute bottom-2 left-[7px] top-2 w-px bg-border" />
              {trail.length === 0 && <p className="text-sm text-muted-foreground">No changes recorded yet.</p>}
              {trail.map((t) => {
                const color = t.action === "created" ? "bg-emerald-400" : t.action === "deleted" ? "bg-red-400" : "bg-blue-400";
                return (
                  <div key={t.id} className="relative pb-5 pl-6">
                    <span className={cn("absolute left-0 top-1.5 h-3.5 w-3.5 rounded-full ring-4 ring-card", color)} />
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">{t.actor_name}</span>
                      <Badge variant="outline" className="font-mono text-[10px] uppercase">{t.action}</Badge>
                      <span className="font-mono text-sm text-primary">RO #{t.ro_number}</span>
                      <span className="ml-auto font-mono text-[10px] text-muted-foreground">{fmtDate(t.timestamp)}</span>
                    </div>
                    {t.changes?.length > 0 && t.action === "updated" && (
                      <div className="mt-2 space-y-1">
                        {t.changes.map((c, i) => (
                          <div key={i} className="font-mono text-[11px] text-muted-foreground">
                            <span className="text-slate-400">{c.field}:</span>{" "}
                            <span className="text-red-300/80 line-through">{String(c.old ?? "∅").slice(0, 40)}</span>{" → "}
                            <span className="text-emerald-300">{String(c.new ?? "∅").slice(0, 40)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* TECHNICIAN USAGE */}
        <TabsContent value="technician" className="mt-5">
          <div className="rounded-xl border border-border bg-card/90 shadow-lg">
            <div className="flex items-center gap-2 border-b border-border px-5 py-4">
              <Wrench className="h-4 w-4 text-primary" />
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Technician Assist Usage</h2>
            </div>
            <div className="overflow-x-auto">
              <Table data-testid="technician-usage-table">
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Technician</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Dealership</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">RO #</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Status</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Score</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">When</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {techUsage.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">No technician activity yet.</TableCell></TableRow>
                  )}
                  {techUsage.map((u) => (
                    <TableRow key={u.id} className="border-border">
                      <TableCell>
                        <span className="font-semibold">{u.user_name}</span>
                        <span className="block font-mono text-[10px] text-muted-foreground">{u.user_email}</span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{u.dealership || "—"}</TableCell>
                      <TableCell className="font-mono text-sm text-primary">{u.ro_number || "—"}</TableCell>
                      <TableCell><StatusBadge status={u.status} /></TableCell>
                      <TableCell className={cn("font-mono font-bold", scoreColor(u.score))}>{u.score}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(u.timestamp)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* HIGH-EXPOSURE CLAIMS */}
        <TabsContent value="exposure" className="mt-5 space-y-4">
          <div className="rounded-xl border border-border bg-card/90 shadow-lg">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-primary" />
                <div>
                  <h2 className="font-heading text-xl font-bold uppercase tracking-tight">High-Exposure Claims</h2>
                  <p className="text-xs text-muted-foreground">≥ {highExp?.min_hours ?? minHours} labor hrs OR high-dollar parts (≥ {highExp?.min_parts ?? 3} parts / major assembly). Recalls &amp; PDIs excluded.</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs font-mono uppercase text-muted-foreground">Min hrs</label>
                <Input type="number" step="0.5" min="0" value={minHours} data-testid="exposure-min-hours"
                  onChange={(e) => setMinHours(e.target.value === "" ? "" : Number(e.target.value))}
                  className="h-9 w-20 bg-secondary/40" />
                <Button variant="outline" size="sm" onClick={exportExposureCsv} data-testid="exposure-export-csv" disabled={!highExp?.rows?.length}>
                  <FileText className="h-4 w-4" /> Export CSV
                </Button>
              </div>
            </div>

            {highExp?.frequency?.length > 0 && (
              <div className="flex flex-wrap gap-2 border-b border-border px-5 py-3" data-testid="exposure-frequency">
                <span className="text-xs font-mono uppercase text-muted-foreground">Tech # frequency:</span>
                {highExp.frequency.map((f) => (
                  <Badge key={f.technician} variant="outline" className="font-mono">#{f.technician} · {f.count}</Badge>
                ))}
              </div>
            )}

            <div className="overflow-x-auto">
              <Table data-testid="exposure-table">
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">RO #</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Tech #</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Labor Op</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Hours</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Parts</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Vehicle</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Status</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Score</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Flagged For</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!highExp?.rows || highExp.rows.length === 0) && (
                    <TableRow><TableCell colSpan={9} className="py-8 text-center text-sm text-muted-foreground">No high-exposure claims match the current filter.</TableCell></TableRow>
                  )}
                  {(highExp?.rows || []).map((r) => (
                    <TableRow key={r.id} className="border-border">
                      <TableCell className="font-mono text-sm text-primary">{r.ro_number || "—"}</TableCell>
                      <TableCell className="font-mono text-sm">{r.technicians.length ? r.technicians.map((t) => `#${t}`).join(", ") : "—"}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{r.labor_ops.length ? r.labor_ops.join(", ") : "—"}</TableCell>
                      <TableCell className="font-mono text-sm font-bold">{r.labor_hours_total || "—"}</TableCell>
                      <TableCell className="font-mono text-sm">{r.parts_count}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.vehicle || "—"}</TableCell>
                      <TableCell><StatusBadge status={r.compliance_status} /></TableCell>
                      <TableCell className={cn("font-mono font-bold", scoreColor(r.score))}>{r.score == null ? "—" : r.score}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{r.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="border-t border-border px-5 py-3 text-[11px] text-muted-foreground">
              Note: labor hours, technician # and parts come from the deterministic scan. Repair orders reviewed before this update may show blank hours/tech until re-uploaded.
            </p>
          </div>
        </TabsContent>

        {/* LOGIN SECURITY */}
        <TabsContent value="security" className="mt-5">
          <div className="rounded-xl border border-border bg-card/90 shadow-lg">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                <div>
                  <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Login Security</h2>
                  <p className="text-xs text-muted-foreground">Every sign-in with location. A red flag means a different city or country than that person's previous sign-in.</p>
                </div>
              </div>
              <Button variant={flaggedOnly ? "default" : "outline"} size="sm" data-testid="security-flagged-toggle"
                onClick={() => setFlaggedOnly((v) => !v)}>
                <AlertTriangle className="h-4 w-4" /> {flaggedOnly ? "Showing flagged only" : "Show flagged only"}
              </Button>
            </div>
            <div className="overflow-x-auto">
              <Table data-testid="login-events-table">
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">User</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Role</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Location</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">IP</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">When</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loginEvents.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">No sign-ins recorded yet.</TableCell></TableRow>
                  )}
                  {loginEvents.map((e) => {
                    const loc = [e.city, e.region, e.country].filter(Boolean).join(", ") || "Unknown";
                    return (
                      <TableRow key={e.id} className={cn("border-border", e.flagged && "bg-red-500/5")}>
                        <TableCell>
                          <span className="font-semibold">{e.name}</span>
                          <span className="block font-mono text-[10px] text-muted-foreground">{e.email}</span>
                        </TableCell>
                        <TableCell className="font-mono text-xs uppercase text-muted-foreground">{roleLabel(e.role)}</TableCell>
                        <TableCell className="text-sm">{loc}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{e.ip || "—"}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(e.timestamp)}</TableCell>
                        <TableCell>
                          {e.flagged ? (
                            <Badge variant="outline" className="border-red-500/40 bg-red-500/15 font-mono text-[10px] uppercase text-red-300">
                              <AlertTriangle className="mr-1 h-3 w-3" /> New location
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/10 font-mono text-[10px] uppercase text-emerald-300">OK</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <p className="border-t border-border px-5 py-3 text-[11px] text-muted-foreground">
              Sign-ins from a new city or country are flagged and an email alert is sent to administrators. Access is never blocked — the person still completes normal 2-step verification.
            </p>
          </div>
        </TabsContent>
      </Tabs>

      <RepairOrderDialog open={dialogOpen} onOpenChange={setDialogOpen} record={editing} onSaved={load} />
    </AppLayout>
  );
}
